import { useDispatch } from 'react-redux';

import { Text } from '../../../shared/uiComponents';
import { BACKGROUND, ORANGE, TEXT } from '../../../shared/Helpers/colors';
import { findEventColor, useShowBTsSessions } from '../Helpers/functions';
import SessionStatusIcon from './sessionStatusIcon';

import { DashboardClockSvg } from '../../../shared/Assets/svgIcons';
import { DispatchProperties, useSelector } from '../../../redux/store';
import {
	CalendarEventItem,
	DiscretionaryEventDataProperties,
	EventDataProperties,
	isDiscretionaryEvent,
} from '../../../redux/API/ClientAPIHelpers/calendarProperties';
import {
	setInfoDiscretionary,
	setInfoDiscretionaryData,
	setInfoEvent,
	setInfoEventData,
} from '../../../redux/State/clientSlice/calendarHelperSlice';
import { sessionTypeColors } from '../Helpers/constants';
import { formatClientName } from '../../../shared/Helpers/functions';
import { isElcFunder } from '../../../redux/API/constants';
import { DEFAULT_TIMEZONE_ID, getWallClockTimeZoneAbbreviation, parseWallClockTime } from '../../../shared/Helpers/timezones';

const DISCRETIONARY_BG = sessionTypeColors.find((x) => x.type === 9)?.color;
const RECURRING_SCHEDULE_OPACITY = 0.4;

// Session times are stored as the client's local wall-clock time. When the client's zone differs from
// the default (Eastern), append a short DST-aware abbreviation (e.g. "(CDT)") so a provider viewing the
// calendar in another timezone knows the time shown is the client's local time. The event's own time is
// used as the reference date so the abbreviation reflects the correct DST period.
const getTzSuffix = (timeZoneId: string | undefined, referenceTime: string) =>
	timeZoneId && timeZoneId !== DEFAULT_TIMEZONE_ID
		? ` (${getWallClockTimeZoneAbbreviation(timeZoneId, referenceTime)})`
		: '';
// True only for actual ELC-funded sessions (session type's funder is ELC), not merely for
// ELC-flagged clients — so the calendar tag no longer appears on a client's insurance/DOE sessions.
const isElcSession = (event: EventDataProperties) => isElcFunder(event.sessionType?.funderId);

const DiscretionaryInfo = ({ event }: { event: DiscretionaryEventDataProperties }) => (
	<>
		<DashboardClockSvg />
		<Text title={event.description} className="name" type="pxs" fontWeight="medium" />
		<Text title={parseWallClockTime(event.startTime).format('hh:mm A')} type="pxs" fontWeight="medium" />
	</>
);

const DiscretionaryInfoExtended = ({ event }: { event: DiscretionaryEventDataProperties }) => (
	<>
		<div className="icon">
			<DashboardClockSvg />
		</div>
		<div>
			<Text title={event.description} className="name" type="psm" fontWeight="semiBold" />
			<Text
				title={`${parseWallClockTime(event.startTime).format('hh:mm A')} - ${parseWallClockTime(event.endTime).format('hh:mm A')}`}
				type="pxs"
				fontWeight="medium"
				color={TEXT.TERTIARY}
			/>
		</div>
	</>
);

const DiscretionaryEvent = ({ event }: { event: DiscretionaryEventDataProperties }) => {
	const dispatch = useDispatch<DispatchProperties>();
	const view = useSelector((state) => state.calendarHelper.calendarView);

	const onDiscretionaryClick = () => {
		dispatch(setInfoDiscretionary(true));
		dispatch(setInfoDiscretionaryData(event));
	};

	const Info = view === 2 ? DiscretionaryInfoExtended : DiscretionaryInfo;

	return (
		<div
			className={`session ${view === 2 ? 'sessionInfoExtended' : 'sessionInfo'}`}
			style={{ background: DISCRETIONARY_BG }}
			onClick={onDiscretionaryClick}
		>
			<div className="tooltip">
				<Text title="Logged Hours" type="pxs" fontWeight="semiBold" color={BACKGROUND.PRIMARY[100]} />
				<Text title={event.description} type="psm" fontWeight="medium" color={BACKGROUND.PRIMARY[100]} />
				<Text title={`Provider: ${event.user.fullName}`} type="pxs" fontWeight="medium" color={BACKGROUND.PRIMARY[100]} />
				<Text
					title={`${parseWallClockTime(event.startTime).format('hh:mm A')} - ${parseWallClockTime(event.endTime).format('hh:mm A')}`}
					type="pxs"
					color={BACKGROUND.PRIMARY[72]}
				/>
				{event.notes && <Text title={event.notes} type="pxs" color={BACKGROUND.PRIMARY[72]} />}
			</div>
			<Info event={event} />
		</div>
	);
};

// Rendered as a leading marker (rather than the trailing "(ELC)" suffix used elsewhere) so the
// designation survives the single-line clamp on the name at narrow/mobile widths, where a trailing
// suffix would be the first thing cut off.
const ElcBadge = () => <span className="elcBadge">ELC</span>;

const SessionInfo = ({ event }: { event: EventDataProperties }) => (
	<>
		<SessionStatusIcon event={event} />
		{isElcSession(event) && <ElcBadge />}
		<Text title={event.client.fullName} className="name" type="pxs" fontWeight="medium" />
		<Text
			title={`${parseWallClockTime(event.startTime).format('hh:mm A')}${getTzSuffix(event.client.timeZoneId, event.startTime)}`}
			type="pxs"
			fontWeight="medium"
		/>
	</>
);

const SessionInfoExtended = ({ event }: { event: EventDataProperties }) => (
	<>
		<div className="icon">
			<SessionStatusIcon event={event} />
		</div>
		<div>
			<div className="nameRow">
				{isElcSession(event) && <ElcBadge />}
				<Text
					title={`${event.client.fullName} - ${event.sessionType.name}`}
					className="name"
					type="psm"
					fontWeight="semiBold"
				/>
			</div>
			<Text
				title={`${parseWallClockTime(event.startTime).format('hh:mm A')} - ${parseWallClockTime(event.endTime).format('hh:mm A')}${getTzSuffix(event.client.timeZoneId, event.startTime)}`}
				type="pxs"
				fontWeight="medium"
				color={TEXT.TERTIARY}
			/>
		</div>
	</>
);

const SessionEvent = ({ event }: { event: EventDataProperties }) => {
	const { sessionType, startTime, client, endTime, isCompleted, user, reportStatus } = event;
	const dispatch = useDispatch<DispatchProperties>();

	const view = useSelector((state) => state.calendarHelper.calendarView);
	const userId = useSelector((state) => state.account.user.id);
	const sessionOpacity = useShowBTsSessions(user.id, userId);
	const opacity = event.isRecurringScheduleSession ? sessionOpacity * RECURRING_SCHEDULE_OPACITY : sessionOpacity;
	const hasPending = event.hasPendingCancellation;
	const isCancelled = event.isCancelled;
	const isCancelledApproved = event.isCancelledApproved;

	const onSessionClick = () => {
		dispatch(setInfoEvent(true));
		dispatch(setInfoEventData(event));
	};

	const SessionType = view === 2 ? SessionInfoExtended : SessionInfo;

	const sessionClassName = [
		'session',
		view === 2 ? 'sessionInfoExtended' : 'sessionInfo',
		hasPending ? 'sessionPending' : '',
		isCancelled ? 'sessionCancelled' : '',
	].filter(Boolean).join(' ');

	return (
		<div
			className={sessionClassName}
			style={{
				background: findEventColor(sessionType.type),
				opacity: isCancelled ? 0.5 : opacity,
				...(hasPending ? { border: `2px dashed ${ORANGE.BASE}` } : {}),
				}}
			onClick={onSessionClick}
		>
			<div className="tooltip">
				<Text title={formatClientName(client.fullName, client.isElc)} type="psm" fontWeight="medium" color={BACKGROUND.PRIMARY[100]} />
				<Text title={`Provider: ${user.fullName}`} type="pxs" fontWeight="medium" color={BACKGROUND.PRIMARY[100]} />
				<Text
					title={`${parseWallClockTime(startTime).format('hh:mm A')} - ${parseWallClockTime(endTime).format('hh:mm A')}${getTzSuffix(client.timeZoneId, startTime)}`}
					type="pxs"
					color={BACKGROUND.PRIMARY[72]}
				/>
				<Text title={sessionType.name} type="pxs" color={BACKGROUND.PRIMARY[72]} />
				{isCancelled ? (
					<Text title="Cancelled" type="pxs" fontWeight="medium" color="#fca5a5" />
				) : (
					<>
						<Text
							title={`Status: ${isCompleted ? 'Completed' : 'Not Completed'}`}
							type="pxs"
							color={BACKGROUND.PRIMARY[72]}
						/>
						{sessionType.requiresSoapNote && (
							<Text
								title={`Soap note: ${reportStatus ? reportStatus.name : 'Not created'}`}
								type="pxs"
								color={BACKGROUND.PRIMARY[72]}
							/>
						)}
					</>
				)}
			</div>
			<SessionType event={event} />
		</div>
	);
};

const Session = ({ event }: { event: CalendarEventItem }) => {
	if (isDiscretionaryEvent(event)) {
		return <DiscretionaryEvent event={event} />;
	}

	return <SessionEvent event={event} />;
};

export default Session;
